import { OUTREACH_DEPARTMENT, prospectingOf } from '@norum/contracts';
import type { Me } from '@/api/session';
import type { Glyph } from '@/ui/icons';

/**
 * What the rail offers, per scope (ADR-0140; the package's `navForScope`
 * and `typesForScope`, `docs/frontend/prospecting-outbound/data/briefing.js`).
 *
 * A scope is one department, or the superadmin's Control room. Each shows
 * exactly its own doors and opens nothing else, default-deny: a department's
 * rail is the prototype's list for it and nothing else, so switching
 * departments changes the whole rail rather than trimming a shared one, and
 * the Control room is company-wide oversight alone, with no department's
 * work in it — no mail, no requests, no records, no prospecting. Every department view starts with the company's own records
 * (Companies, Contacts; Suppliers where a department validates or buys
 * from one), because those are the company's, not a department's —
 * Executive's too, where the prototype drew none.
 *
 * The rail offers doors; it decides nothing. Every route still answers from
 * what the session may read, and the admin door still refuses anybody who
 * is not a superadmin. `routeAllowed` is the same list read the other way:
 * a door a scope does not offer is not reached by typing its address.
 */

export type NavCount = 'approvals' | 'workspace' | 'profile_changes';

export interface NavLinkItem {
  kind: 'link';
  to: string;
  label: string;
  /** Match the path exactly, for a door whose children are other doors. */
  end?: boolean;
  /** A number drawn beside it, read live by the rail. */
  count?: NavCount;
}

export interface NavNote {
  kind: 'note';
  text: string;
}

export type NavItem = NavLinkItem | NavNote;

export interface NavGroup {
  /** Absent for the first group, which is Today alone. */
  label?: string;
  items: NavItem[];
}

/** Where a person is working: one department, or the superadmin's Control room over all of them. */
export type Scope = { kind: 'control' } | { kind: 'department'; name: string };

export const CONTROL_ROOM: Scope = { kind: 'control' };

/** The scope's name, as the switcher, the guard and the headers say it. */
export const CONTROL_ROOM_NAME = 'Control room';

export const departmentScope = (name: string): Scope => ({ kind: 'department', name });

/** What the rail knows about the department in view, from the platform. */
export interface NavContext {
  /** Its name in words (`departments/list`). */
  label: string;
  /** `access: read_only` in the ledger, enforced by the gateway (ADR-0037). */
  readOnly: boolean;
  /** The flexible record kinds live in it, in the order it declares them. */
  kinds: Array<{ entity: string; title: string }>;
  /** Insight pages, with the pipeline already dropped where no deals are kept. */
  insights: Array<{ key: string; title: string }>;
}

const link = (to: string, label: string, extra: Partial<Omit<NavLinkItem, 'kind' | 'to' | 'label'>> = {}): NavLinkItem => ({
  kind: 'link',
  to,
  label,
  ...extra,
});

const group = (label: string, items: NavItem[]): NavGroup => ({ label, items });

const TODAY: NavGroup = { items: [link('/today', 'Today', { end: true })] };

/** A flexible kind's door, or the note that no feature keeps it here yet. */
function kindDoor(ctx: NavContext, entity: string, label: string): NavItem {
  const live = ctx.kinds.find((k) => k.entity === entity);
  return live ? link(`/records/${entity}`, label) : { kind: 'note', text: `${label}: no feature is live in ${ctx.label} yet.` };
}

/**
 * The company's own records, first in every department view. Suppliers are
 * a department's validation rows (§6), so the door opens on the department's
 * own; it is offered where a department validates or buys from one.
 */
function records(withSuppliers: boolean): NavGroup {
  return group('Records', [
    link('/companies', 'Companies'),
    link('/contacts', 'Contacts'),
    ...(withSuppliers ? [link('/suppliers', 'Suppliers')] : []),
  ]);
}

function requests(label: string): NavGroup {
  // Shared rooms remain reachable in a read-only department too. Membership
  // and each operation's authority are checked by the server.
  return group(label, [link('/requests', 'Requests'), link('/projects', 'Projects')]);
}

const CONVERSATIONS: NavGroup = group('Asked and answered', [link('/conversations', 'Conversations')]);

/** The prospecting group in a department's own words (PR-23, SG-10). */
function prospecting(department: string): NavGroup {
  const words = prospectingOf(department)!;
  return group('Prospecting', [
    // Setting up is offered to anyone in the scope, as the prototype does;
    // approving what it makes stays the superadmin's (ADR-0134).
    link('/prospecting/setup', 'Set up prospecting'),
    ...(words.outreach ? [link('/markets', 'Markets')] : []),
    link('/profiles', words.profiles),
    ...(words.outreach ? [link('/territories', 'Territories')] : []),
    link('/prospecting/plans', 'Plans'),
    // What an assistant a person signed in proposed, waiting for them (ADR-0153).
    link('/prospecting/shortlists', 'Shortlists'),
    link('/prospects', words.found),
    ...(words.outreach ? [link('/signals', 'Signals')] : []),
    // An investor is reached by an introduction from someone who knows them.
    ...(words.channels.includes('warm_intro') && !words.outreach ? [link('/introductions', 'Introductions')] : []),
  ]);
}

function outbound(ctx: NavContext): NavGroup[] {
  return [
    records(true),
    group('Who we sell to', [link('/buyers', 'Buyers'), link('/strangers', 'Strangers')]),
    prospecting(OUTREACH_DEPARTMENT),
    group('Prospecting follow-through', [
      link('/profile-changes', 'Profile changes', { count: 'profile_changes' }),
      link('/introductions', 'Introductions'),
      link('/said-stop', 'Opted out'),
      link('/our-index', 'Our index'),
    ]),
    group('Market validation', [link('/offers', 'Commercial offers')]),
    // Built for Outbound's outreach (proposal 0007); not in the prototype's rail.
    group('Outreach', [
      link('/first-touches', 'First emails'),
      link('/campaigns', 'Campaigns'),
      link('/lifecycle', 'Lifecycle'),
      link('/mailings', 'Mailings'),
      link('/outreach-report', 'Week by week'),
    ]),
    group('The work', [
      kindDoor(ctx, 'Deal', 'Pipeline'),
      kindDoor(ctx, 'Order', 'Orders'),
      link('/samples', 'Samples'),
      // The shared inbox is a list here, the bridge to old ways; an
      // unanswered message is a slot on Today, which is why it is no tab.
      link('/mail/shared', 'Shared inbox · hello@'),
      // Every conversation the department has, including those that arrived in no shared box, such as a prospect's thread.
      link('/mail', 'All mail'),
    ]),
    requests('With other departments'),
    CONVERSATIONS,
  ];
}

function fundraising(): NavGroup[] {
  return [records(false), group('Investors', [link('/strangers', 'Strangers')]), prospecting('fundraising'), requests('With other departments'), CONVERSATIONS];
}

function foodScience(): NavGroup[] {
  const words = prospectingOf('food_science')!;
  return [
    records(true),
    group('The product', [link('/samples', 'Samples'), link('/conversations', 'Conversations')]),
    group('Suppliers', [
      link('/prospecting/setup', 'Set up prospecting'),
      link('/profiles', words.profiles),
      link('/prospecting/plans', 'Plans'),
      link('/prospecting/shortlists', 'Shortlists'),
      link('/prospects', words.found),
    ]),
    requests('With other departments'),
  ];
}

function production(ctx: NavContext): NavGroup[] {
  return [records(true), group('Fulfilment', [kindDoor(ctx, 'Order', 'Orders'), link('/samples', 'Samples')]), requests('With other departments')];
}

/**
 * A department's own agents and what each is granted, and nobody else's:
 * the pages read the session department when opened here (`here`). The
 * whole roster, every department's, is the Control room's.
 */
const TEAM: NavGroup = group('The team', [link('/agents', 'Agents'), link('/assignments', 'Assignments')]);

function tech(): NavGroup[] {
  // The software factory: Aria and Vero, and what they build is a change a
  // department asked for. The Tool library is the company's, in the Control room.
  return [records(false), TEAM, requests('With other departments'), CONVERSATIONS];
}

function executive(): NavGroup[] {
  // Its agents read no department; they ask, and answers come back as
  // requests. The company's records are its people's as much as anyone's.
  return [records(false), TEAM, requests('Asking the others'), CONVERSATIONS];
}

function finance(ctx: NavContext): NavGroup[] {
  // Read only: nothing here composes, sends or approves. Finance asks.
  return [records(false), group('Money', [kindDoor(ctx, 'Invoice', 'Invoices'), kindDoor(ctx, 'Order', 'Orders')]), requests('Ask another department')];
}

/** A department the prototype never drew: its records, its work, and the doors every department has. */
function other(ctx: NavContext): NavGroup[] {
  const kinds: NavItem[] =
    ctx.kinds.length === 0
      ? [{ kind: 'note', text: `No feature is live in ${ctx.label} yet. Its records appear here the day one ships.` }]
      : ctx.kinds.map((k) => link(`/records/${k.entity}`, k.title));
  return [records(false), group('The work', kinds), requests(ctx.readOnly ? 'Ask another department' : 'With other departments'), CONVERSATIONS];
}

/**
 * Every record kind live in the department has a door, whatever the
 * prototype drew: a feature that shipped Invoices into Outbound is reached
 * from Outbound's rail. A kind not already drawn joins the group that holds
 * the department's record kinds, after them, or a group of its own.
 */
function withEveryLiveKind(groups: NavGroup[], ctx: NavContext): NavGroup[] {
  const drawn = new Set(groups.flatMap((g) => g.items.filter((i): i is NavLinkItem => i.kind === 'link').map((i) => i.to)));
  const extra = ctx.kinds.filter((k) => !drawn.has(`/records/${k.entity}`)).map((k) => link(`/records/${k.entity}`, k.title));
  if (extra.length === 0) return groups;
  const isKind = (i: NavItem) => (i.kind === 'link' ? i.to.startsWith('/records/') : /no feature is live/.test(i.text));
  const home = groups.findIndex((g) => g.items.some(isKind));
  if (home < 0) {
    // After the department's own groups, before what every department has.
    const at = groups.findIndex((g) => g.items.some((i) => i.kind === 'link' && i.to === '/requests'));
    const kept = group('Kept here', extra);
    return at < 0 ? [...groups, kept] : [...groups.slice(0, at), kept, ...groups.slice(at)];
  }
  return groups.map((g, n) => {
    if (n !== home) return g;
    let last = -1;
    g.items.forEach((i, j) => (isKind(i) ? (last = j) : undefined));
    return { ...g, items: [...g.items.slice(0, last + 1), ...extra, ...g.items.slice(last + 1)] };
  });
}

function departmentGroups(name: string, ctx: NavContext): NavGroup[] {
  return withEveryLiveKind(drawnFor(name, ctx), ctx);
}

function drawnFor(name: string, ctx: NavContext): NavGroup[] {
  switch (name) {
    case OUTREACH_DEPARTMENT:
      return outbound(ctx);
    case 'fundraising':
      return fundraising();
    case 'food_science':
      return foodScience();
    case 'production':
      return production(ctx);
    case 'tech':
      return tech();
    case 'executive':
      return executive();
    case 'finance':
      return finance(ctx);
    default:
      return other(ctx);
  }
}

/**
 * Deciding, in a department: its lead approves that department's
 * promotions (§3, §8), and anyone may ask for a change and confirm its
 * read-back. A department that only reads decides nothing here, and a
 * department drawn read-only by the platform is read-only whatever its name.
 */
function decide(me: Me, name: string, readOnly: boolean): NavGroup[] {
  if (readOnly) return [];
  const items: NavItem[] = [
    ...(me.isSuperadmin || me.leads.includes(name) ? [link('/approvals', 'Approvals', { count: 'approvals' })] : []),
    link('/changes', 'Changes'),
    // What the department's agents should always or never do (ADR-0159).
    link('/directions', 'Directions for agents'),
    // Agreements/events keep their department origin. Application and
    // investor coordination belong to Fundraising (proposal 0016).
    link('/registers', name === 'fundraising' ? 'Agreements, events and funding' : 'Agreements and events'),
    ...(name === 'fundraising' ? [link('/institutions', 'Institution programmes')] : []),
  ];
  return [group('Decide', items)];
}

// The constitution and the decisions are the rules the platform is built
// to; they are read in `YOU` by a superadmin (`TECHNICAL_DOORS`), and a
// person works under them without reading them as pages.
const YOU = (): NavGroup =>
  group('You', [
    link('/settings', 'Settings', { end: true }),
    link('/settings/availability', 'When I can be booked'),
    // A person's own asks of their twin (Amendment 0011).
    link('/tasks', 'Tasks'),
    // Their own mail, mirrored for them alone (ADR-0120): never under a
    // department's door, where it once filled the shared inbox.
    link('/mail/mine', 'My inbox'),
    link('/notifications', 'Notifications'),
    link('/ledger/constitution', 'The constitution'),
    link('/ledger/decisions', 'The decisions'),
  ]);

/**
 * The Control room: the superadmin's company-wide oversight, and no
 * department's work. Its home is the Workspace, the one place a superadmin
 * reads what is outstanding (ADR-0117); Today is a day in a department.
 * Every door here reads across departments by who is asking, never by the
 * session's department, so nothing in it is one department's list under
 * the Control room's name.
 */
function controlRoom(ctx: NavContext): NavGroup[] {
  return [
    { items: [link('/workspace', 'Workspace', { end: true, count: 'workspace' })] },
    group('Decide', [
      link('/approvals', 'Approvals', { count: 'approvals' }),
      link('/promotions', 'Promotions'),
      link('/ledger/proposals', 'Ledger edits'),
    ]),
    group('Who can do what', [
      link('/people', 'People'),
      link('/agents', 'Agents'),
      link('/assignments', 'Assignments'),
      link('/tools', 'Tool library'),
      link('/connections', 'Connections'),
      link('/plans', 'Plans and usage'),
    ]),
    group('Oversight', [
      link('/departments', 'Departments'),
      link('/reports', 'Reports'),
      // A pipeline is one department's deals, and is read there.
      ...ctx.insights.filter((i) => i.key !== 'pipeline').map((i) => link(`/insight/${i.key}`, i.title)),
      link('/audit', 'Audit log'),
      link('/erasure', 'Erasure requests'),
      link('/refusals', 'Refusals'),
      link('/readiness', 'Readiness'),
      link('/activity', 'Activity'),
    ]),
    // The platform's own cloud project and repository, kept by the
    // superadmin's twin (Amendment 0013, ADR-0172).
    group('Keep the platform', [link('/operations', 'Operations')]),
    group('Safety', [link('/safety', 'Stop everything'), link('/incidents', 'Incidents')]),
    YOU(),
  ];
}

/** Business ownership for department-only pages. Shared/personal pages are
 * intentionally absent. This registry filters both the sidebar and the guard's
 * owner search; it describes navigation, never a server grant. */
export const DEPARTMENT_PAGE_OWNERS: Readonly<Record<string, readonly string[]>> = {
  '/institutions': ['fundraising'],
  '/mail': ['outbound'],
  '/strangers': ['outbound', 'fundraising'],
  '/markets': ['outbound'],
  '/territories': ['outbound'],
  '/signals': ['outbound'],
  '/profile-changes': ['outbound'],
  '/our-index': ['outbound'],
  '/buyers': ['outbound'],
  '/offers': ['outbound'],
  '/said-stop': ['outbound'],
  '/first-touches': ['outbound'],
  '/campaigns': ['outbound'],
  '/lifecycle': ['outbound'],
  '/mailings': ['outbound'],
  '/outreach-report': ['outbound'],
  '/profiles': ['outbound', 'fundraising', 'food_science'],
  '/prospects': ['outbound', 'fundraising', 'food_science'],
  '/prospecting': ['outbound', 'fundraising', 'food_science'],
  '/introductions': ['outbound', 'fundraising'],
  '/samples': ['outbound', 'food_science', 'production'],
  '/suppliers': ['outbound', 'food_science', 'production'],
};

export function departmentsForPage(path: string): readonly string[] | null {
  if (PERSONAL.some((door) => under(path, door))) return null;
  const door = Object.keys(DEPARTMENT_PAGE_OWNERS).find((candidate) => under(path, candidate));
  return door ? DEPARTMENT_PAGE_OWNERS[door]! : null;
}

export function navFor(me: Me, ctx: NavContext, scope: Scope = departmentScope(me.department)): NavGroup[] {
  const groups: NavGroup[] =
    scope.kind === 'control' && me.isSuperadmin
      ? controlRoom(ctx)
      : [TODAY, ...departmentGroups(scope.kind === 'department' ? scope.name : me.department, ctx), ...decide(me, scope.kind === 'department' ? scope.name : me.department, ctx.readOnly), YOU()];

  const seen = new Set<string>();
  return groups
    // The platform's own doors are the superadmin's alone (`TECHNICAL_DOORS`).
    .map((g) => (me.isSuperadmin ? g : { ...g, items: g.items.filter((i) => i.kind !== 'link' || !TECHNICAL_DOORS.some((d) => under(i.to, d))) }))
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        if (i.kind !== 'link') return true;
        const owners = departmentsForPage(i.to);
        const department = scope.kind === 'department' ? scope.name : me.department;
        if (owners && ((scope.kind === 'control' && me.isSuperadmin) || !owners.includes(department))) return false;
        if (seen.has(i.to)) return false;
        seen.add(i.to);
        return true;
      }),
    }))
    .filter((g) => g.items.some((i) => i.kind === 'link'));
}

/** Every door the rail offers, flat — the command palette's screens. */
export function doorsOf(groups: NavGroup[]): NavLinkItem[] {
  return groups.flatMap((g) => g.items.filter((i): i is NavLinkItem => i.kind === 'link'));
}

/**
 * A person's own pages and the company's rules: reached from every scope,
 * because they are nobody's department. A promotion is proposed by anyone
 * and decided by the CPO, a role and not a department (§9, ROLE_SCOPES);
 * an erasure is recorded by anyone (ADR-0015).
 */
const PERSONAL = ['/chat', '/tasks', '/promotions', '/settings', '/notifications', '/erasure', '/mail/mine'];
/**
 * Technical detail is the superadmin's (ADR-0140): the platform's own
 * pages — the team's agents and grants, the roster, the rulebook — are
 * doors for a superadmin alone, in whichever view they stand. Everyone else
 * is shown what they work on.
 */
const TECHNICAL_DOORS = ['/agents', '/assignments', '/people', '/ledger/constitution', '/ledger/decisions'];
/**
 * The explicitly shared department framework. Department-owned work is
 * admitted only by that department's drawn doors, never by a generic
 * path-prefix fallback. Shared readers keep their session-scoped rows.
 */
export const SHARED_DEPARTMENT_PAGES = ['/today', '/conversations', '/contacts', '/companies', '/requests', '/projects'] as const;
/** Company-wide oversight: the Control room's. */
const OVERSIGHT = ['/approvals', '/agents', '/assignments', '/tools', '/departments', '/audit', '/refusals', '/reports', '/incidents', '/activity', '/insight'];
/** The superadmin's alone, in the Control room. */
/** A queue row's department is provenance for these records, not a switch
 * destination. Their decisions live in company-wide oversight. */
export function controlRoomRecordPage(path: string): boolean {
  return ['/incidents', '/ledger/proposals'].some((door) => under(path, door));
}

const SUPERADMIN = ['/workspace', '/ledger/proposals', '/connections', '/plans', '/readiness', '/safety', '/operations'];

/**
 * The superadmin's pages that say so themselves, in their own words and
 * with where to ask instead. Shown those words rather than the guard's
 * "not one of this scope's doors", which reads as if another department
 * had it. The page reads nothing it may not: core-api refuses the reads.
 */
const SAYS_ITS_OWN = ['/workspace', '/connections', '/plans', '/readiness', '/safety', '/operations'];

export function saysItsOwnRefusal(path: string, me: Me): boolean {
  return !me.isSuperadmin && SAYS_ITS_OWN.some((d) => under(path, d));
}

const under = (path: string, door: string) => path === door || path.startsWith(`${door}/`);

/**
 * Default-deny, read the other way round (`typesForScope`): a scope opens
 * its own doors and a person's own pages, and nothing else.
 *
 *   the Control room  its doors, the company-wide oversight and the
 *                     superadmin's pages; never a department's work, which
 *                     would render the session department's rows under the
 *                     Control room's name
 *   a department      its doors and the explicit shared framework
 *                     (Today, companies, contacts, requests, projects); the
 *                     company-wide oversight only for its lead who is no
 *                     superadmin, and so has no Control room
 *
 * A page opened in the wrong scope is not refused into a dead end: the
 * guard says where it belongs and opens it there (`ownersOf`).
 */
export function routeAllowed(path: string, me: Me, scope: Scope, groups: NavGroup[]): boolean {
  if (path === '/' || PERSONAL.some((d) => under(path, d))) return true;
  // One person's page is work as well as oversight: the person, or a lead of
  // a department they work in, arranges cover there (ADR-0114). The roster
  // list stays the superadmin's; the server says what the page shows.
  if (/^\/people\/[0-9a-f-]{36}$/i.test(path)) return true;
  if (TECHNICAL_DOORS.some((d) => under(path, d))) {
    if (!me.isSuperadmin) return false;
    // The roster and the rulebook follow the superadmin everywhere; the team
    // opens where its door is — the Control room, or Tech's and Executive's.
    return ['/people', '/ledger/constitution', '/ledger/decisions'].some((d) => under(path, d)) || scope.kind === 'control' || doorsOf(groups).some((d) => under(path, d.to));
  }
  if (doorsOf(groups).some((d) => under(path, d.to))) return true;
  if (scope.kind === 'control') {
    if (!me.isSuperadmin) return false;
    // The pipeline is one department's deals, so it is read in that
    // department and never under the Control room's name.
    if (under(path, '/insight/pipeline')) return false;
    return OVERSIGHT.some((d) => under(path, d)) || SUPERADMIN.some((d) => under(path, d));
  }
  if (SHARED_DEPARTMENT_PAGES.some((d) => under(path, d))) return true;
  // A lead who is no superadmin decides their department's drafts and reads
  // its pipeline, which is work; the rest of the oversight — the log, the
  // refusals, the tools, the costs — is technical, and the superadmin's.
  return !me.isSuperadmin && me.leads.includes(scope.name) && (under(path, '/approvals') || under(path, '/insight/pipeline'));
}

/** Where a scope opens: the Control room on its Workspace, a department on Today. */
export const homeOf = (scope: Scope): string => (scope.kind === 'control' ? '/workspace' : '/today');

/**
 * The scopes, of this person's, where a page opened in the wrong one
 * belongs: the Control room first for its own pages, then each department
 * whose rail offers it. `groupsFor` draws a scope's rail as the rail would.
 */
export function ownersOf(path: string, me: Me, groupsFor: (scope: Scope) => NavGroup[], scopes: readonly Scope[]): Scope[] {
  return scopes.filter((s) => {
    const groups = groupsFor(s);
    // Only a door drawn there, or the Control room's own pages: a shared
    // page (mail, requests) belongs to every department and names none.
    if (doorsOf(groups).some((d) => under(path, d.to))) return true;
    return s.kind === 'control' && routeAllowed(path, me, s, groups) && !PERSONAL.some((d) => under(path, d));
  });
}

export interface Tab {
  to: string;
  label: string;
  glyph: Glyph;
  count?: NavCount;
}

/**
 * The phone's tabs, per scope (B3). In the Control room a superadmin
 * decides, stops and reads; the header's menu opens everything else, so the
 * bar has no Menu cell. In a department its lead approves and reads
 * records; a member reads records. Mail is never a tab (B2).
 */
export function tabsFor(
  me: Me,
  scope: Scope = departmentScope(me.department),
  /** Whether the scope's rail has an Approvals door (a read-only department has none). */
  approvalsDoor = true,
): { tabs: Tab[]; menu: boolean } {
  const approvals: Tab = { to: '/approvals', label: 'Approvals', glyph: 'approvals', count: 'approvals' };
  if (scope.kind === 'control' && me.isSuperadmin) {
    return {
      tabs: [
        { to: '/workspace', label: 'Workspace', glyph: 'calendar', count: 'workspace' },
        approvals,
        { to: '/safety', label: 'Stop', glyph: 'stop' },
        { to: '/reports', label: 'Reports', glyph: 'reports' },
      ],
      menu: false,
    };
  }
  const today: Tab = { to: '/today', label: 'Today', glyph: 'calendar' };
  const records: Tab = { to: '/companies', label: 'Records', glyph: 'records' };
  const here = scope.kind === 'department' ? scope.name : me.department;
  if (approvalsDoor && (me.isSuperadmin || me.leads.includes(here))) return { tabs: [today, approvals, records], menu: true };
  return { tabs: [today, records], menu: true };
}
